import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { isEmpty, get } from "lodash";
import { withTranslation, useTranslation } from "react-i18next";
import { countries } from "countries-list";
import BookingInfoImage from "./BookingInfoImage";
import { usePreCheckInContext } from "./contexts/PreCheckInContext";
import Property from "../../model/property";
import nationalities from "./GuestSteps/nationalities.json";
import StripeVerifyService from "../../backend/StripeVerifyService";
import StripeVerifyButton from "../../components/StripeVerifyButton";
import { ClipLoaderIcon } from "../../components/LoadingIcon";
import { convertToAmPm } from "../../utils/timeUtils";

const BookDetail = () => {
  const [defaultFlow, setDefaultFlow] = useState(false);
  const {
    propertyUid,
    bookingNumber,
    language,
    userEmail,
    data: {
      property,
      guestService,
      configuration,
      presets,
      accessToken,
      booking,
      guests,
    },
    components: { PreCheckInHeader, PreCheckInContainer },
    func: {
      updateGuests,
      updateErrorModal,
      clearLocalStorage,
      setAccessTokenData,
    },
  } = usePreCheckInContext();
  const history = useHistory();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const unloadCallback = (event) => {
      event.preventDefault();
      if (!event.returnValue) {
        const histories = [...Property.getHistoryStack()];
        clearLocalStorage();
        Property.setHistoryStack(histories);
        event.returnValue = "";
      }
      return "";
    };

    window.addEventListener("beforeunload", unloadCallback);
    return () => window.removeEventListener("beforeunload", unloadCallback);
  }, [clearLocalStorage]);

  useEffect(() => {
    if (configuration && !defaultFlow) {
      setDefaultFlow(!get(configuration, "preset_config.enable_stripe"));
    }
  }, [defaultFlow, configuration]);

  const goToGuestDetail = (step, guest) => {
    let url = `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}/guests/${guest}/steps/${step}?language=${language}&token=${accessToken}`;
    if (!isEmpty(userEmail)) url += `&email=${encodeURIComponent(userEmail)}`;
    history.push(url);
  };

  const continueBtn = () => {
    if (guestService.allowRequestNumberOfKeys) {
      goToPickupKeys();
    } else if (configuration.paymentType) {
      goToCardList();
    } else {
      goToAdditionalInformation();
    }
  };

  const goToAdditionalInformation = () => {
    history.push(
      `/pre-checkin/${propertyUid}/booking/${bookingNumber}/additional-information?language=${language}&email=${encodeURIComponent(
        userEmail
      )}&token=${accessToken}&email=${encodeURIComponent(userEmail)}`
    );
  };

  const goToPickupKeys = () => {
    history.push(
      `/pre-checkin/${propertyUid}/booking/${bookingNumber}/pickup-keys?language=${language}&email=${encodeURIComponent(
        userEmail
      )}&token=${accessToken}&email=${encodeURIComponent(userEmail)}`
    );
  };

  const goToCardList = () => {
    history.push(
      `/pre-checkin/${propertyUid}/booking/${bookingNumber}/cards?language=${language}&email=${encodeURIComponent(
        userEmail
      )}&token=${accessToken}&email=${encodeURIComponent(userEmail)}`
    );
  };

  const updateClientSecretOfGuest = (clientSecret, guestNumber) => {
    if (guests.guests && guests.guests[guestNumber]) {
      guests.guests[guestNumber].clientSecret = clientSecret;
      guests.guests[guestNumber].stripeSubmitted = true;
      updateGuests(guests);
    }
  };

  const onCompleteIdentify = async (clientSecret, guestNumber) => {
    if (defaultFlow) {
      goToGuestDetail(1, guestNumber + 1);
      return;
    }
    if (guests.guests[guestNumber].verified === true) {
      history.push(
        `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}/guests/${
          guestNumber + 1
        }/data-collection?language=${language}&token=${accessToken}&email=${encodeURIComponent(
          userEmail
        )}`
      );
      return;
    }
    try {
      const { data } = await StripeVerifyService.retrieveVerificationSession(
        accessToken,
        propertyUid,
        clientSecret
      );
      if (data && data.verification_identify) {
        const guestDocument = data.verification_identify;
        if (guestDocument.status === "verified") {
          if (!guestService.disableIdVerification) {
            guests.guests[guestNumber].firstName = guestDocument.first_name;
            guests.guests[guestNumber].lastName = guestDocument.last_name;
          } else {
            if (!guests.guests[guestNumber].mainGuest) {
              guests.guests[guestNumber].firstName = "";
              guests.guests[guestNumber].lastName = "";
            }
          }

          if (guestDocument.issuing_country) {
            guests.guests[guestNumber].countryCode =
              guestDocument.issuing_country;
            guests.guests[guestNumber].country =
              countries[guestDocument.issuing_country].name;
            guests.guests[guestNumber].nationalityCode =
              guestDocument.issuing_country;
            const getNationality = nationalities.filter(
              (nationality) =>
                nationality.alpha_2_code === guestDocument.issuing_country
            );
            if (
              getNationality.length > 0 &&
              getNationality[0] &&
              getNationality[0].nationality
            )
              guests.guests[guestNumber].nationality =
                getNationality[0].nationality;
          }
          guests.guests[guestNumber].passport = guestDocument.number;
          guests.guests[guestNumber].idType = guestDocument.type;
          guests.guests[guestNumber].verified =
            guestDocument.status === "verified";
          if (guestDocument.dob) {
            guests.guests[guestNumber].birthday =
              guestDocument.dob.year +
              "/" +
              guestDocument.dob.month +
              "/" +
              guestDocument.dob.day;
          }
          if (guestDocument.expiration_date) {
            guests.guests[guestNumber].dateOfExpiry =
              guestDocument.expiration_date.year +
              "/" +
              guestDocument.expiration_date.month +
              "/" +
              guestDocument.expiration_date.day;
          }
          guests.guests[guestNumber].clientSecret = clientSecret;
          guests.guests[guestNumber].stripeSubmitted = true;
          updateGuests(guests);

          history.push(
            `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}/guests/${
              guestNumber + 1
            }/data-collection?language=${language}&token=${accessToken}`
          );
        } else if (guestDocument.status === "processing") {
          setTimeout(() => onCompleteIdentify(clientSecret, guestNumber), 7000);
        } else if (guestDocument.status === "requires_input") {
          guests.guests[guestNumber].clientSecret = "";
          guests.guests[guestNumber].stripeSubmitted = false;
          updateGuests(guests);
        }
      }
    } catch (error) {
      guests.guests[guestNumber].clientSecret = "";
      guests.guests[guestNumber].stripeSubmitted = false;
      guests.guests[guestNumber].verified = false;
      let reason =
        get(error, "response.data.error.reason") || "The document is invalid!";
      updateGuests(guests);
      updateErrorModal(true, reason);
    }
  };

  const onDeclineIdentify = (clientSecret, guestNumber) => {
    guests.guests[guestNumber].clientSecret = "";
    guests.guests[guestNumber].verified = false;
    guests.guests[guestNumber].stripeSubmitted = false;
    updateGuests(guests);
  };

  const confirmationPage = () => {
    history.push(
      `/pre-checkin/${propertyUid}/booking/${bookingNumber}/request-completed?language=${language}`
    );
  };

  const countEditableGuests = () => {
    if (!guests || booking.atLeastGuestRequired <= 1) return 1;

    const editableGuests = guests.allGuests().filter((guest) => guest.editable);
    return editableGuests.length;
  };

  const updateUserNationality = (guestNumber, userNationality) => {
    if (guests.guests && guests.guests[guestNumber]) {
      guests.guests[guestNumber].userNationality = userNationality;
      updateGuests(guests);
    }
  };

  return (
    <>
      <PreCheckInContainer containerName="guest-list-management-book-detail">
        <PreCheckInHeader
          title={guestService?.title || t("PRE_CHECK_IN", "PRE CHECK-IN")}
          headerLink
        />
        {booking && configuration && guestService && accessToken && (
          <>
            {booking.isBookingActive ? (
              <>
                <section
                  id="content"
                  className="relative flex flex-col px-9 w-full flex-1 md:flex-0 pt-6">
                  <div className="flex flex-col">
                    <div className="flex mb-10 w-full">
                      <div
                        className="mr-8 w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden flex items-center"
                        style={{
                          backgroundColor: presets.backgroundColorTopBar,
                        }}>
                        {property.property_system_icon && (
                          <img
                            className="w-full"
                            src={property.property_system_icon}
                            alt={t("HOTEL_LOGO", "Hotel Logo")}
                          />
                        )}
                      </div>
                      <div className="capitalize font-normal">
                        <h1
                          className="mb-2 type-normal large"
                          style={{ color: presets.titleColor }}>
                          {property.name}
                        </h1>
                        {!guestService.hide_room_type && <p
                          className="mb-2 type-regular medium"
                          style={{ color: presets.subtitleColor }}>
                          {get(booking, "room_description")}
                        </p>}
                        <p
                          className="mb-2 type-regular medium"
                          style={{ color: presets.subtitleColor }}>
                          {t("BOOKING_NUMBER")} {get(booking, "booking_id")}
                        </p>
                        <p
                          className="mt-8 type-regular medium"
                          style={{ color: presets.subtitleColor }}>
                          {configuration.checkPreCheckInInterfaceTypeIsHotelKey
                            ? t("BEDS", "Beds")
                            : t("ROOMS")}
                          :
                          <span className="font-bold pl-1">
                            {get(booking, "number_of_rooms", 1)}
                          </span>{" "}
                          |{" "}
                          {configuration.checkPreCheckInInterfaceTypeIsHotelKey
                            ? t("MAX_GUESTS", "Max Guests")
                            : t("GUESTS")}
                          :
                          <span className="font-bold">
                            {" "}
                            {get(booking, "total_guests", 1)}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="md:max-w-7xl mx-auto w-full mb-4">
                      <div className="capitalize font-normal flex w-full mb-7">
                        <div className="flex flex-1 justify-end">
                          <div className="flex flex-col space-y-1">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-9 h-9 mx-auto mb-4"
                              viewBox="0 0 448 512">
                              <path
                                fill={`${presets.backgroundColorTopBar}`}
                                d="M148 288h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm108-12v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm96 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm-96 96v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm-96 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm192 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm96-260v352c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V112c0-26.5 21.5-48 48-48h48V12c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v52h128V12c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v52h48c26.5 0 48 21.5 48 48zm-48 346V160H48v298c0 3.3 2.7 6 6 6h340c3.3 0 6-2.7 6-6z"></path>
                            </svg>
                            <p
                              className="text-okkami-gray text-center type-regular medium uppercase"
                              style={{ color: presets.subtitleColor }}>
                              {t("CHECK_IN")}
                            </p>
                            <p
                              style={{ color: presets.titleColor }}
                              className="text-okkami-dark-gray text-center type-title normal uppercase">
                              {booking.checkInDateText}
                            </p>
                            <p
                              className="text-center type-normal normal"
                              style={{ color: presets.titleColor }}>
                              {`${property.isEvermore ? t("BEGINS_AT", "") : ""} ${presets.amPm
                                ? convertToAmPm(configuration.checkInTimeText)
                                : configuration.checkInTimeText}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-center items-center mx-8 pt-4">
                          <svg
                            className="w-7 h-7 mx-auto"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512">
                            <path
                              fill={`${presets.backgroundColorTopBar}`}
                              d="M190.5 66.9l22.2-22.2c9.4-9.4 24.6-9.4 33.9 0L441 239c9.4 9.4 9.4 24.6 0 33.9L246.6 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.2-22.2c-9.5-9.5-9.3-25 .4-34.3L311.4 296H24c-13.3 0-24-10.7-24-24v-32c0-13.3 10.7-24 24-24h287.4L190.9 101.2c-9.8-9.3-10-24.8-.4-34.3z"></path>
                          </svg>
                        </div>
                        <div className="flex flex-1 justify-start">
                          <div className="flex flex-col space-y-1">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-9 h-9 mx-auto mb-4"
                              viewBox="0 0 448 512">
                              <path
                                fill={`${presets.backgroundColorTopBar}`}
                                d="M148 288h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm108-12v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm96 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm-96 96v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm-96 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm192 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm96-260v352c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V112c0-26.5 21.5-48 48-48h48V12c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v52h128V12c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v52h48c26.5 0 48 21.5 48 48zm-48 346V160H48v298c0 3.3 2.7 6 6 6h340c3.3 0 6-2.7 6-6z"></path>
                            </svg>
                            <p
                              className="text-okkami-gray font-normal text-center type-regular medium uppercase"
                              style={{ color: presets.subtitleColor }}>
                              {t("CHECK_OUT")}
                            </p>
                            <p
                              style={{ color: presets.titleColor }}
                              className="text-okkami-dark-gray text-center type-title normal uppercase">
                              {booking.checkOutDateText}
                            </p>
                            <p
                              className="text-center type-normal normal"
                              style={{ color: presets.titleColor }}>
                              {presets.amPm
                                ? convertToAmPm(configuration.checkOutTimeText)
                                : configuration.checkOutTimeText}
                            </p>
                          </div>
                        </div>
                      </div>
                      {guests &&
                        guests.allGuests().map((elementInArray, index) => {
                          if (!elementInArray.editable) {
                            return null;
                          }
                          return (
                            <div
                              className="w-full mb-20 rounded-3xl h-72 p-8 shadow-xl relative"
                              key={index}
                              style={{
                                backgroundColor: presets.backgroundColorTopBar,
                                color: presets.appHomeTextDetailsPrecheckin,
                              }}>
                              {elementInArray.completed && (
                                <div className="absolute -top-5 -right-5">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-16 w-16 bg-white rounded-full"
                                    viewBox="0 0 512 512"
                                    fill="#32C33D"
                                    stroke="#fff">
                                    <path
                                      fill="#32C33D"
                                      d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
                                  </svg>
                                </div>
                              )}
                              <div className="flex flex-col">
                                <p
                                  className="text-center type-title normal"
                                  style={{
                                    color: presets.appHomeTextDetailsPrecheckin,
                                  }}>
                                  {index === 0 &&
                                    t("PRIMARY_GUEST", "Primary Guest Info")}
                                  {index > 0 &&
                                    t(
                                      "ADDITIONAL_GUEST",
                                      "Additional Guest Info"
                                    )}
                                </p>
                                <div className="flex justify-between text-12 py-10">
                                  <p
                                    className="type-title normal uppercase"
                                    style={{
                                      color:
                                        presets.appHomeTextDetailsPrecheckin,
                                    }}>
                                    {t("NAME")}
                                  </p>
                                  <p
                                    className="type-regular normal"
                                    style={{
                                      color:
                                        presets.appHomeTextDetailsPrecheckin,
                                    }}>
                                    {elementInArray.title}{" "}
                                    {elementInArray.firstName}{" "}
                                    {elementInArray.lastName}
                                  </p>
                                </div>
                                <hr style={{ backgroundColor: "#DBDBDB" }} />
                                <div className="flex justify-between text-12 py-10">
                                  <p
                                    className="type-title normal uppercase"
                                    style={{
                                      color:
                                        presets.appHomeTextDetailsPrecheckin,
                                    }}>
                                    {t("GUEST_EMAIL", "GUEST EMAIL")}
                                  </p>
                                  <p
                                    className="type-regular normal uppercase"
                                    style={{
                                      color:
                                        presets.appHomeTextDetailsPrecheckin,
                                    }}>
                                    {elementInArray.email}
                                  </p>
                                </div>
                              </div>
                              <div className="text-center">
                                {!elementInArray.preCheckedInCompleted && (
                                  <StripeVerifyButton
                                    primaryColor={
                                      presets.app_home_color_primary
                                    }
                                    buttonStyleName={presets.buttonStyleName}
                                    token={accessToken}
                                    propertyUid={propertyUid}
                                    isHotelKey={configuration.checkPreCheckInInterfaceTypeIsHotelKey}
                                    bookingNumber={bookingNumber}
                                    language={language}
                                    clientSecret={elementInArray.clientSecret}
                                    stripeSubmitted={
                                      elementInArray.stripeSubmitted
                                    }
                                    verified={elementInArray.verified}
                                    onLoading={(res) => setLoading(res)}
                                    setAccessTokenData={setAccessTokenData}
                                    onClickCallback={(clientSecret) => {
                                      setLoading(true);
                                      updateClientSecretOfGuest(
                                        clientSecret,
                                        index
                                      );
                                    }}
                                    onCompleteCallback={(clientSecret) => {
                                      onCompleteIdentify(clientSecret, index);
                                      setLoading(false);
                                    }}
                                    onDeclineCallback={(clientSecret) => {
                                      onDeclineIdentify(clientSecret, index);
                                      setLoading(false);
                                    }}
                                    raiseErrorOnPublishableKey={() => {
                                      setDefaultFlow(true);
                                      setLoading(false);
                                    }}
                                    skipped={
                                      !get(
                                        configuration,
                                        "preset_config.enable_stripe"
                                      )
                                    }
                                    raiseError={(error) => {
                                      updateErrorModal(true, error);
                                      setLoading(false);
                                    }}
                                    actionText={
                                      elementInArray.completed
                                        ? t("EDIT_DETAILS")
                                        : index === 0
                                        ? t("CONFIRM_DETAILS")
                                        : t("PROVIDE_DETAILS")
                                    }
                                    className={`bg-white app-outline-button type-title medium ${presets.buttonStyleName}`}
                                    styles={{
                                      color: presets.backgroundColorTopBar,
                                      borderColor:
                                        presets.backgroundColorTopBar,
                                    }}
                                    userNationality={
                                      elementInArray.userNationality
                                    }
                                    updateUserNationality={(userNationality) =>
                                      updateUserNationality(
                                        index,
                                        userNationality
                                      )
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                    {guests &&
                      guests.numberOfGuests() > 0 &&
                      guests.allGuests().filter((g) => g.completed === true)
                        .length >= countEditableGuests() && (
                        <button
                          className={`app-button-ipad type-title medium ${presets.buttonStyleName}`}
                          style={{
                            backgroundColor: presets.backgroundColorTopBar,
                            color: presets.appHomeTextCallToAction,
                          }}
                          onClick={() => continueBtn()}>
                          {t("CONTINUE")}
                        </button>
                      )}
                    <p
                      className="py-4 leading-7 text-center"
                      style={{
                        color: presets.subtitleColor,
                      }}>
                      {t("REVIEW_AND_CONFIRM_MESSAGE", "")}
                    </p>
                  </div>
                </section>
                {guests &&
                  guests.numberOfGuests() > 0 &&
                  guests.allGuests().filter((g) => g.completed === true)
                    .length >= countEditableGuests() && (
                    <button
                      className="app-button-mobile type-title medium "
                      style={{
                        backgroundColor: presets.backgroundColorTopBar,
                        color: presets.appHomeTextCallToAction,
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                      }}
                      onClick={() => continueBtn()}>
                      {t("CONTINUE")}
                    </button>
                  )}
              </>
            ) : (
              <section
                id="content"
                className="relative flex flex-col px-9 w-full flex-1 md:flex-0">
                <div className="flex flex-col justify-center">
                  {guestService.bookingInfoImage && (
                    <div className="-mx-9">
                      <BookingInfoImage
                        bookingInfoImage={guestService.bookingInfoImage}
                        propertyName={property.name}
                      />
                    </div>
                  )}
                  <div className="flex-1 w-full flex flex-col p-6">
                    <div
                      className="w-full mb-20 rounded-3xl h-72 p-8 shadow-lg relative max-w-2xl mx-auto"
                      style={{
                        backgroundColor: presets.backgroundColorTopBar,
                        height: "26rem",
                      }}>
                      <div className="flex flex-col">
                        <div className="text-center pb-6">
                          <p
                            className="type-title normal uppercase"
                            style={{
                              color: presets.appHomeTextDetailsPrecheckin,
                            }}>
                            {property.name}
                          </p>
                        </div>
                        <hr style={{ backgroundColor: "#DBDBDB" }} />
                        <div className="flex justify-between text-12 py-10">
                          <p
                            className="type-title normal uppercase"
                            style={{
                              color: presets.appHomeTextDetailsPrecheckin,
                            }}>
                            {t("DATE")}
                          </p>
                          <p
                            className="text-white type-regular normal"
                            style={{
                              color: presets.appHomeTextDetailsPrecheckin,
                            }}>
                            {booking.checkInDateText} -{" "}
                            {booking.checkOutDateText}
                          </p>
                        </div>
                        <hr style={{ backgroundColor: "#DBDBDB" }} />
                        <div className="flex justify-between text-12 py-10">
                          <p
                            className="type-title normal uppercase"
                            style={{
                              color: presets.appHomeTextDetailsPrecheckin,
                            }}>
                            {t("BOOKING_NO", "BOOKING NO.")}
                          </p>
                          <p
                            className="text-white type-regular normal"
                            style={{
                              color: presets.appHomeTextDetailsPrecheckin,
                            }}>
                            {bookingNumber}
                          </p>
                        </div>
                        {!guestService.hide_room_type && <hr style={{ backgroundColor: "#DBDBDB" }} />}
                        {!guestService.hide_room_type && <div className="flex justify-between text-12 py-10">
                          <p
                            className="type-title normal uppercase"
                            style={{
                              color: presets.appHomeTextDetailsPrecheckin,
                            }}>
                            {t("ROOM_TYPE", "ROOM TYPE")}
                          </p>
                          <p
                            className="text-white type-regular normal"
                            style={{
                              color: presets.appHomeTextDetailsPrecheckin,
                            }}>
                            {booking.room_description}
                          </p>
                        </div>}
                      </div>
                      {[
                        "pre_check_in_completed",
                        "pre_checkin_completed",
                      ].includes(booking.status) && (
                        <div className="text-center">
                          <button
                            className={`bg-white app-outline-button type-title medium ${presets.buttonStyleName}`}
                            style={{
                              color: presets.backgroundColorTopBar,
                              borderColor: presets.backgroundColorTopBar,
                            }}
                            onClick={() => confirmationPage()}>
                            {t("CHECK_IN_COMPLETED")}
                          </button>
                        </div>
                      )}
                      {booking.status === "room_is_ready" && (
                        <div className="text-center mx-auto">
                          <button
                            className={`bg-white app-outline-button type-title medium ${presets.buttonStyleName}`}
                            style={{
                              color: presets.backgroundColorTopBar,
                              borderColor: presets.backgroundColorTopBar,
                            }}
                            onClick={() => confirmationPage()}>
                            {t("ROOM_IS_READY")}
                          </button>
                        </div>
                      )}

                      </div>
                    {["waiting_for_pre_checkin"].includes(booking.status) && configuration && (
                      <div className="text-center">
                          {t("WAITING_FOR_PRE_CHECKIN", {
                            pre_checkin_windows: String(configuration.preCheckInWindow)
                              .replace('h', ' hours')
                              .replace("same_day", "on the day")
                              .replace("_", " ")
                          })}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}
            {loading && (
              <div className="fixed inset-0 flex justify-center items-center bg-gray-500 opacity-50 z-50">
                <ClipLoaderIcon
                  loadingState={loading}
                  size={30}
                  color={presets.backgroundColorTopBar}
                />
              </div>
            )}
          </>
        )}
      </PreCheckInContainer>
    </>
  );
};
export default withTranslation()(BookDetail);
